import {
  DEST_LABEL,
  SETTING_GROUP_KEYS,
  STAGE_LABEL,
  destOf,
  fmtText,
  junLabel,
  type SettingGroupKey,
  type TaskRow,
} from '@kdk/shared';
import { tokens } from '@kdk/shared/theme';
import { Box, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useTasks, useSchedule } from '../../api/queries';
import { AsyncBoundary } from '../../shared-ui/AsyncBoundary';
import { DrawerSkeleton, ListSkeleton } from '../../shared-ui/skeletons';
import { DestChip } from '../../shared-ui/DestChip';
import { DetailDrawer, type BackLink } from '../../shared-ui/DetailDrawer';
import { KeyValueList } from '../../shared-ui/KeyValueList';
import { stageStarts } from './model/stageStarts';

const GROUP_TITLE: Readonly<Record<SettingGroupKey, string>> = {
  hq: '本社検討（案）',
  sen: '線路G',
  mu: '無線G',
  ki: '機器G',
};

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <Box sx={{ borderTop: `1px solid ${tokens.line}`, pt: '12px', mt: '14px' }}>
    <Typography variant="h3" sx={{ color: 'text.secondary', mb: '8px' }}>
      {title}
    </Typography>
    {children}
  </Box>
);

const ScheduleSection = ({ taskId }: { taskId: string }) => {
  const schedule = useSchedule();
  return (
    <Section title="推進スケジュール">
      <AsyncBoundary
        state={schedule}
        skeleton={<ListSkeleton rows={2} rowHeight={32} testId="skeleton-schedule-section" />}
      >
        {(s) => {
          const row = s.rows.find((r) => r.taskId === taskId);
          const starts = row === undefined ? [] : stageStarts(row.stages);
          return starts.length === 0 ? (
            <Typography color="text.secondary">スケジュールは未設定です。</Typography>
          ) : (
            <>
              {starts.map(({ stage, slot }) => (
                <Stack
                  key={stage}
                  direction="row"
                  sx={{ alignItems: 'center', gap: '10px', my: '4px' }}
                >
                  <Box
                    component="i"
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: '2px',
                      bgcolor: tokens.stage[stage],
                    }}
                  />
                  <Box sx={{ width: 110 }}>{STAGE_LABEL[stage]}</Box>
                  <Typography color="text.secondary">{junLabel(slot)}〜</Typography>
                </Stack>
              ))}
            </>
          );
        }}
      </AsyncBoundary>
    </Section>
  );
};

const TaskBody = ({ task }: { task: TaskRow }) => (
  <>
    <Typography sx={{ color: tokens.dim }}>
      {task.phase}　›　{task.gyomu}
    </Typography>
    <Typography variant="h2" sx={{ fontSize: 19, mt: '4px', mb: '6px' }}>
      {task.task}
    </Typography>
    {task.issue !== null && (
      <Typography color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
        {task.issue}
      </Typography>
    )}
    {SETTING_GROUP_KEYS.map((key) => {
      const setting = task.groups[key];
      if (
        setting.now === null &&
        setting.next === null &&
        setting.note === null &&
        setting.dx === null
      ) {
        return null;
      }
      const dest = destOf(task, key);
      const priority =
        setting.priority1 === null ? '' : `　優先 ${setting.priority1}-${setting.priority2 ?? '—'}`;
      return (
        <Section key={key} title={`${GROUP_TITLE[key]}${priority}`}>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: '6px' }}>
            <DestChip dest="G" label="現状：現場" />
            <span>→</span>
            <DestChip dest={dest} label={`今後：${DEST_LABEL[dest]}`} />
            {setting.standardization !== null && (
              <DestChip dest="x" label={setting.standardization} />
            )}
          </Stack>
          <KeyValueList
            items={[
              ...(setting.dx === null ? [] : [{ label: 'DX化対象', value: setting.dx }]),
              ...(setting.note === null ? [] : [{ label: '実施方法・課題', value: setting.note }]),
            ]}
          />
        </Section>
      );
    })}
    <Section title={`DX化アイデア${task.ideaNo === null ? '' : `（No.${task.ideaNo}）`}`}>
      {task.ideas.length === 0 ? (
        <Typography color="text.secondary">未記入</Typography>
      ) : (
        task.ideas.map((idea, i) => (
          <Box key={`${idea.text}-${i}`} sx={{ mb: 1 }}>
            <b>{idea.text}</b>
            {idea.source !== null && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{idea.source}</Typography>
            )}
            {idea.target !== null && (
              <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                対象：{idea.target}
              </Typography>
            )}
          </Box>
        ))
      )}
      {task.ideaMemo !== null && (
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
          備考：{fmtText(task.ideaMemo)}
        </Typography>
      )}
    </Section>
    <ScheduleSection taskId={task.id} />
  </>
);

export const TaskDrawer = ({
  id,
  onClose,
  back,
}: {
  id: string;
  onClose: () => void;
  back?: BackLink | undefined;
}) => {
  const tasks = useTasks();
  const title =
    tasks.status === 'success' ? (tasks.data.find((t) => t.id === id)?.task ?? '作業') : '作業';
  return (
    <DetailDrawer title={title} onClose={onClose} back={back}>
      <AsyncBoundary state={tasks} skeleton={<DrawerSkeleton />}>
        {(list) => {
          const task = list.find((t) => t.id === id);
          return task === undefined ? (
            <Typography color="text.secondary" sx={{ py: 4 }}>
              該当する作業が見つかりません。
            </Typography>
          ) : (
            <TaskBody task={task} />
          );
        }}
      </AsyncBoundary>
    </DetailDrawer>
  );
};
